import { create } from 'zustand';
import { getBibleVersions, getChapter, searchBible } from '../lib/api';
import { BibleVersion, BiblePassage } from '../services/bibleService';

interface BiblePanel {
  id: string;
  bibleId: string;
  currentReference?: string;
}

interface BibleState {
  panels: BiblePanel[];
  versions: BibleVersion[];
  loading: boolean;
  error: string | null;
  loadVersions: () => Promise<void>;
  search: (query: string) => Promise<BiblePassage[]>;
  setVersion: (version: string) => void;
  addPanel: (bibleId: string) => void;
  removePanel: (panelId: string) => void;
}

export const useBibleStore = create<BibleState>((set, get) => ({
  panels: [],
  versions: [],
  loading: false,
  error: null,

  loadVersions: async () => {
    try {
      set({ loading: true, error: null });
      const versions = await getBibleVersions();
      set({ versions, loading: false });

      // Initialize with KJV if no panels exist
      if (get().panels.length === 0) {
        const kjv = versions.find(v => v.id === 'kjv');
        if (kjv) {
          get().addPanel(kjv.id);
        }
      }
    } catch (error) {
      set({ error: 'Failed to load Bible versions', loading: false });
    }
  },

  search: async (query: string) => {
    try {
      set({ loading: true, error: null });
      const results = await searchBible(get().panels[0]?.bibleId || 'kjv', query);
      set({ loading: false });
      return results;
    } catch (error) {
      set({ error: 'Search failed', loading: false });
      return [];
    }
  },

  setVersion: (version: string) => {
    // Clear the cache for the current passage
    const queryClient = window.__REACT_QUERY_GLOBAL_CLIENT__;
    if (queryClient) {
      queryClient.invalidateQueries({ queryKey: ['bible-passage'] });
    }

    // Update the version in the store
    set(state => ({
      panels: state.panels.map((panel, index) => 
        index === 0 ? { ...panel, bibleId: version } : panel
      )
    }));
  },

  addPanel: (bibleId: string) => {
    set(state => ({
      panels: [...state.panels, { id: Date.now().toString(), bibleId }]
    }));
  },

  removePanel: (panelId: string) => {
    set(state => ({
      panels: state.panels.filter(panel => panel.id !== panelId)
    }));
  }
}));